import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, forkJoin, from, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import moment from 'moment';
import { MasterModel } from '../../../../master/models/master.model';
import { MasterService } from '../../../../master/services/master.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { AdvancePaymentService } from '../../../../../modules/expense/advancepayment/advancePayment.service';
import { LoadingDialogService } from '../../../../../shared/ui/loading/loading-dialog.service';
import { ActualSelected, Utils } from '../../../../../shared/utils';
import { BILL_METHODS, consts } from '../../../../../consts';
import { ContactService } from '../../../../../modules/contact/contact/contact.service';
import { PaymentCardService } from '../../../../../modules/finanzas/paymentcard/paymentcard.service';
import { BailService } from '../../../../../modules/expense/bail/bail.service';

const BANK_PAYMENTS = [
  consts.TYPEPAY_BANKTRANSFERENCE,
  consts.TYPEPAY_SEPA,
  consts.TYPE_TARJETA,
  consts.TYPEPAY_CONFIRMING,
  consts.TYPEPAY_PAGARES,
];

const SUPPLIER_PAY_METHODS = [
  consts.TYPEPAY_CASH,
  consts.TYPEPAY_CREDITCARD_PAY,
  consts.TYPE_DEBITCARD,
  consts.TYPEPAY_DEBITENTRY,
  consts.TYPEPAY_BANKTRANSFERENCE,
  consts.TYPEPAY_CONFIRMING,
  consts.TYPEPAY_PAGARES,
  consts.TYPE_SOCIO,
];

const SUPPLIER_BANK_PAYMENTS = [
  consts.TYPEPAY_BANKTRANSFERENCE,
  consts.TYPEPAY_DEBITENTRY,
  consts.TYPEPAY_CONFIRMING,
  consts.TYPEPAY_PAGARES,
];

const SUPPLIER_CARD_PAYMENTS = [
  consts.TYPEPAY_CREDITCARD_PAY,
  consts.TYPE_DEBITCARD,
];

@Component({
  selector: 'app-customer-advance-payment-form',
  templateUrl: './customer-advance-payment-form.component.html',
  styleUrls: ['./customer-advance-payment-form.component.css'],
  standalone: false
})
export class CustomerAdvancePaymentFormV2Component implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  uid: string | null = null;

  isLoading = true;
  isSaving = false;
  submitted = false;
  isAssigned = false;
  assignedTo = '';

  isSupplier = false;
  isBail = false;
  payMethods: string[] = BILL_METHODS;
  contacts: MasterModel[] = [];
  taxes: MasterModel[] = [];
  concepts: MasterModel[] = [];
  cashRegisters: MasterModel[] = [];
  bankAccounts: MasterModel[] = [];
  partners: MasterModel[] = [];
  paymentCards: MasterModel[] = [];

  subtotal = 0;
  taxesTotal = 0;
  retentionsTotal = 0;
  total = 0;
  taxesLabel = 'Impuestos';
  lineTotals: number[] = [];

  private readonly CHUNK = 30;
  visibleCount = this.CHUNK;

  private loaded: any = null;
  private taxById = new Map<string, any>();
  private readonly lazyLookups = new Map<string, Promise<void>>();
  private lastValidTaxesByLineKey: any = {};
  private prevTaxesByLineKey: any = {};
  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private masterService: MasterService,
    private advancePaymentService: AdvancePaymentService,
    private loading: LoadingDialogService,
    private notification: NotificationService,
    private contactService: ContactService,
    private paymentCardService: PaymentCardService,
    private bailService: BailService,
  ) {
    this.form = this.fb.group({
      contactUid: [null, Validators.required],
      date: [new Date(), Validators.required],
      typePay: [null, Validators.required],
      cashRegisterUid: [null],
      bankAccountUid: [null],
      partnerUid: [null],
      paymentCardUid: [null],
      duration: [null],
      lines: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    const mode = this.route.snapshot.data ? this.route.snapshot.data['mode'] : null;
    this.isBail = mode === 'bail';
    this.isSupplier = mode === 'supplier' || this.isBail;
    if (this.isBail) this.form.get('duration').setValidators(Validators.required);
    if (this.isSupplier) this.payMethods = SUPPLIER_PAY_METHODS;
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.uid = params['uid'] || null;
      this.load();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get lines(): UntypedFormArray {
    return this.form.get('lines') as UntypedFormArray;
  }

  get visibleLines(): any[] {
    return this.lines.controls.slice(0, this.visibleCount);
  }

  onLinesScroll(ev: any): void {
    const el = ev && ev.target;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200 && this.visibleCount < this.lines.length) {
      this.visibleCount = Math.min(this.visibleCount + this.CHUNK, this.lines.length);
    }
  }

  private async load(): Promise<void> {
    this.isLoading = true;
    try {
      const [lookups, advancePayment] = await Promise.all([
        firstValueFrom(forkJoin({
          contacts: this.isSupplier ? from(this.loadProviders()) : this.masterService.getContacts(),
          taxes: this.masterService.getTaxes(),
          concepts: this.masterService.getConcepts(),
        })),
        this.uid ? this.service.loadData(this.uid) : Promise.resolve(null),
      ]);

      this.contacts = lookups.contacts || [];
      this.taxes = lookups.taxes || [];
      this.concepts = lookups.concepts || [];
      this.buildTaxIndex();

      if (advancePayment) {
        await this.ensurePaymentLookups(advancePayment.typePay);
        this.applyAdvancePayment(advancePayment);
      } else {
        this.addLine();
      }

      this.wireForm();
      this.applyPaymentValidators();
      this.recomputeTotals();
      if (this.isAssigned) this.form.disable({ emitEvent: false });
      this.isLoading = false;
    } catch {
      this.isLoading = false;
      this.notification.errorBottom(this.uid ? `No se pudo cargar ${this.isBail ? 'la fianza' : 'el anticipo'}.` : 'Error cargando datos del formulario.');
      if (this.uid) this.router.navigate([this.listRoute]);
    }
  }

  private applyAdvancePayment(data: any): void {
    this.loaded = data;
    this.isAssigned = !!data.invoiceUid && data.invoiceUid !== consts.EMPTY_GUID;
    this.assignedTo = data.expenseNumber || '';

    const contact = this.contacts.find(c => this.sameId(c.id, data.providerUid));
    const cash = this.cashRegisters.find(c => c.metadata && c.metadata.id === data.efectivoId);
    const bank = this.bankAccounts.find(b => this.sameId(b.id, data.typePayBankAccount));
    const partner = this.partners.find(p => String(p.id) === String(data.partnersId));
    const card = this.paymentCards.find(c => this.sameId(c.id, data.typePayBankAccount));

    this.form.patchValue({
      contactUid: contact || (data.providerName ? { id: data.providerUid, display: data.providerName } : null),
      date: data.date ? new Date(data.date) : null,
      typePay: data.typePay || null,
      cashRegisterUid: cash || null,
      bankAccountUid: bank || null,
      partnerUid: partner || null,
      paymentCardUid: card || null,
      duration: data.duration || null,
    }, { emitEvent: false });

    this.lines.clear({ emitEvent: false });
    const apiLines = ((this.isBail ? data.bailLine : data.advancedPaymentLines) || []).slice().sort((a: any, b: any) => (a.position || 0) - (b.position || 0));
    for (const apiLine of apiLines) {
      const group = this.createLine();
      const concept = this.concepts.find(c => c.display === apiLine.product);
      group.patchValue({
        conceptUid: concept || (apiLine.product ? { id: apiLine.product, display: apiLine.product } : null),
        quantity: Number(apiLine.quantity) || 0,
        price: Number(apiLine.unityAmount) || 0,
        discount: Number(apiLine.discount) || 0,
        tax: this.mapApiTaxes(apiLine.taxes || []),
      }, { emitEvent: false });
      this.lines.push(group, { emitEvent: false });
    }
    if (this.lines.length === 0) this.lines.push(this.createLine(), { emitEvent: false });
  }

  private mapApiTaxes(apiTaxes: any[]): any[] {
    return apiTaxes
      .map(t => this.taxes.find(x => x.metadata && t.taxId != null && Number(x.metadata.id) === Number(t.taxId))
        || this.taxes.find(x => this.sameId(x.id, t.uid)))
      .filter(Boolean);
  }

  private wireForm(): void {
    this.form.get('typePay').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyPaymentValidators();
      this.ensurePaymentLookups(this.form.get('typePay').value).then(() => this.applyDefaultPaymentAccount());
    });

    this.form.valueChanges
      .pipe(debounceTime(150), takeUntil(this.destroy$))
      .subscribe(() => this.recomputeTotals());
  }

  private applyPaymentValidators(): void {
    const rules: { [key: string]: boolean } = {
      cashRegisterUid: this.isCashRegisterPayment(),
      bankAccountUid: this.isBankAccountPayment(),
      partnerUid: this.isPartnerPayment(),
      paymentCardUid: this.isCardPayment(),
    };
    Object.keys(rules).forEach(key => {
      const ctrl = this.form.get(key);
      if (rules[key]) ctrl.setValidators(Validators.required);
      else ctrl.clearValidators();
      ctrl.updateValueAndValidity({ emitEvent: false });
    });
  }

  private applyDefaultPaymentAccount(): void {
    if (this.isCashRegisterPayment() && !this.form.get('cashRegisterUid').value && this.cashRegisters.length > 0) {
      this.form.get('cashRegisterUid').setValue(this.cashRegisters[0]);
    }
    if (this.isBankAccountPayment() && !this.form.get('bankAccountUid').value && this.bankAccounts.length === 1) {
      this.form.get('bankAccountUid').setValue(this.bankAccounts[0]);
    }
    if (this.isPartnerPayment() && !this.form.get('partnerUid').value && this.partners.length === 1) {
      this.form.get('partnerUid').setValue(this.partners[0]);
    }
    if (this.isCardPayment() && !this.form.get('paymentCardUid').value && this.paymentCards.length === 1) {
      this.form.get('paymentCardUid').setValue(this.paymentCards[0]);
    }
  }

  private ensureLookup(key: string, load: () => Promise<void>): Promise<void> {
    let entry = this.lazyLookups.get(key);
    if (!entry) {
      entry = load().catch(() => {
        this.lazyLookups.delete(key);
      });
      this.lazyLookups.set(key, entry);
    }
    return entry;
  }

  private ensurePaymentLookups(typePay: string): Promise<void> {
    const tasks: Promise<void>[] = [];
    if (typePay === consts.TYPEPAY_CASH) {
      tasks.push(this.ensureLookup('cashRegisters', () => firstValueFrom(this.masterService.getCashRegisters()).then(list => { this.cashRegisters = list || []; })));
    }
    if (this.bankPayments.indexOf(typePay) !== -1) {
      tasks.push(this.ensureLookup('bankAccounts', () => firstValueFrom(this.masterService.getBankAccounts()).then(list => {
        this.bankAccounts = (list || []).map(account => ({
          ...account,
          display: account.metadata && account.metadata.bank ? `${account.metadata.bank} — ${account.display}` : account.display,
        }));
      })));
    }
    if (this.isSupplier && SUPPLIER_CARD_PAYMENTS.indexOf(typePay) !== -1) {
      tasks.push(this.ensureLookup('paymentCards', () => this.paymentCardService.getList(0).then(result => {
        this.paymentCards = ((result && result.list) || []).map((card: any) => ({
          id: card.uid,
          display: card.number ? card.name + ' — ' + card.number : card.name,
        }));
      })));
    }
    if (typePay === consts.TYPE_SOCIO) {
      tasks.push(this.ensureLookup('partners', () => firstValueFrom(this.masterService.getParnerts()).then(list => { this.partners = list || []; })));
    }
    return Promise.all(tasks).then(() => undefined);
  }

  isCashRegisterPayment(): boolean {
    return this.form.get('typePay').value === consts.TYPEPAY_CASH;
  }

  isBankAccountPayment(): boolean {
    return this.bankPayments.indexOf(this.form.get('typePay').value) !== -1;
  }

  isCardPayment(): boolean {
    return this.isSupplier && SUPPLIER_CARD_PAYMENTS.indexOf(this.form.get('typePay').value) !== -1;
  }

  get bankPayments(): string[] {
    return this.isSupplier ? SUPPLIER_BANK_PAYMENTS : BANK_PAYMENTS;
  }

  get listRoute(): string {
    if (this.isBail) return '/expense/advancepayment/bail';
    return this.isSupplier ? '/expense/advancepayment/provider' : '/invoice/advancepaymentinvoice';
  }

  get service(): any {
    return this.isBail ? this.bailService : this.advancePaymentService;
  }

  private loadProviders(): Promise<MasterModel[]> {
    return this.contactService.getListProviders().then(list => (list || []).map((contact: any) => ({
      id: contact.uid,
      display: contact.companyName,
    })));
  }

  isPartnerPayment(): boolean {
    return this.form.get('typePay').value === consts.TYPE_SOCIO;
  }

  private createLine(): UntypedFormGroup {
    return this.fb.group({
      conceptUid: [null, Validators.required],
      quantity: [1, Validators.required],
      price: [0, Validators.required],
      discount: [0],
      tax: [[]],
    });
  }

  addLine(): void {
    this.lines.push(this.createLine());
    this.visibleCount = this.lines.length;
    this.recomputeTotals();
  }

  removeLine(index: number): void {
    this.lines.removeAt(index);
    this.recomputeTotals();
  }

  dropLine(event: any): void {
    const moved = this.lines.at(event.previousIndex);
    this.lines.removeAt(event.previousIndex);
    this.lines.insert(event.currentIndex, moved);
    this.recomputeTotals();
  }

  onConceptChange(line: any): void {
    const value = line.get('conceptUid').value;
    const concept: any = value ? this.concepts.find(c => this.sameId(c.id, value.id)) : null;
    if (!concept) return;

    const metadata: any = concept.metadata || {};
    line.get('quantity').setValue(1);
    line.get('price').setValue(metadata.netPrice || 0);
    line.get('discount').setValue(metadata.discount || 0);
    line.get('tax').setValue((metadata.taxes || [])
      .map((t: any) => this.taxes.find(tax => tax.metadata && tax.metadata.id == t.taxId))
      .filter(Boolean));
  }

  onSave(): void {
    if (this.isSaving || this.isAssigned) return;

    this.submitted = true;
    this.applyPaymentValidators();
    this.form.markAllAsTouched();
    this.lines.controls.forEach(line => {
      const control = line.get('conceptUid');
      if (control.value && !this.lineProduct(control.value)) control.setErrors({ required: true });
    });

    const outsideFiscalYear = this.form.get('date').value && !Utils.DateIsInFiscalYear(this.form.get('date').value);
    if (this.form.invalid || this.lines.length === 0 || outsideFiscalYear) {
      this.showValidationErrors(outsideFiscalYear);
      return;
    }

    this.recomputeTotals();
    const value = this.form.getRawValue();
    const contact = this.contacts.find(c => this.sameId(c.id, value.contactUid && value.contactUid.id));
    if (!contact) {
      this.notification.errorBottom(this.isSupplier ? 'Selecciona un proveedor de la lista.' : 'Selecciona un cliente de la lista.');
      return;
    }

    const cash = this.isCashRegisterPayment() ? this.cashRegisters.find(c => this.sameId(c.id, value.cashRegisterUid && value.cashRegisterUid.id)) : null;
    const bank = this.isBankAccountPayment() ? this.bankAccounts.find(b => this.sameId(b.id, value.bankAccountUid && value.bankAccountUid.id)) : null;
    const partner = this.isPartnerPayment() ? this.partners.find(p => this.sameId(p.id, value.partnerUid && value.partnerUid.id)) : null;
    const card = this.isCardPayment() ? this.paymentCards.find(c => this.sameId(c.id, value.paymentCardUid && value.paymentCardUid.id)) : null;

    const dto: any = {
      invoiceUid: this.loaded && this.loaded.invoiceUid ? this.loaded.invoiceUid : consts.EMPTY_GUID,
      providerUid: contact.id,
      providerName: contact.display,
      date: moment(value.date).format('YYYY-MM-DD'),
      concept: this.loaded ? this.loaded.concept : null,
      base: this.subtotal,
      total: this.total,
      expenseNumber: this.loaded ? this.loaded.expenseNumber : null,
      isInvoice: !this.isSupplier,
      activityUid: ActualSelected.activitySelected,
      fiscalYear: Number(ActualSelected.yearSelected),
      typePay: value.typePay,
      typePayBankAccount: bank ? bank.id : card ? card.id : null,
      efectivoId: cash && cash.metadata ? cash.metadata.id : null,
      partnersId: partner ? Number(partner.id) || 0 : 0,
    };
    if (this.isBail) {
      dto.bailLine = this.buildLines(value.lines);
      dto.duration = Number(value.duration);
    } else {
      dto.advancedPaymentLines = this.buildLines(value.lines);
    }
    if (this.uid) dto.uid = this.uid;

    const request = this.uid
      ? this.service.editData(this.uid, dto)
      : this.service.addData(dto);

    this.isSaving = true;
    this.loading.run(this.isBail ? (this.uid ? 'Guardando fianza…' : 'Creando fianza…') : (this.uid ? 'Guardando anticipo…' : 'Creando anticipo…'), request)
      .then(() => {
        this.notification.okBottom(this.isBail ? 'Fianza guardada correctamente' : 'Anticipo guardado correctamente');
        this.router.navigate([this.listRoute]);
      })
      .catch(error => {
        this.notification.errorBottom(error && error.error && error.error.message ? error.error.message : (this.isBail ? 'Error guardando la fianza.' : 'Error guardando el anticipo.'));
      })
      .finally(() => this.isSaving = false);
  }

  private lineProduct(value: any): string {
    if (!value) return '';
    if (typeof value === 'string') return value.trim();
    return value.display ? String(value.display).trim() : '';
  }

  private buildLines(lines: any[]): any[] {
    return (lines || []).map((line, index) => {
      const quantity = Number(line.quantity) || 0;
      const unityAmount = Number(line.price) || 0;
      const discount = Number(line.discount) || 0;
      const base = unityAmount * quantity * (1 - discount / 100);

      const taxes = (line.tax || [])
        .map((t: any) => this.findTaxFull(t))
        .filter(Boolean)
        .map((tax: any) => ({
          uid: tax.id,
          name: tax.metadata && tax.metadata.name ? tax.metadata.name : tax.display,
          taxImport: tax.metadata && tax.metadata.import != null ? Number(tax.metadata.import) : 0,
          type: tax.metadata && tax.metadata.type ? tax.metadata.type : '',
          taxId: tax.metadata && tax.metadata.id ? Number(tax.metadata.id) : 0,
        }));

      const taxAmount = taxes.reduce((sum: number, tax: any) => sum + base * (tax.taxImport / 100), 0);

      return {
        product: this.lineProduct(line.conceptUid),
        quantity: quantity,
        discount: discount,
        unityAmount: unityAmount,
        totalLine: base + taxAmount,
        position: index,
        conceptId: 0,
        taxes: taxes,
      };
    });
  }

  private showValidationErrors(outsideFiscalYear: boolean): void {
    const messages: string[] = [];
    if (this.form.get('contactUid').invalid) messages.push(this.isSupplier ? 'Selecciona el proveedor.' : 'Selecciona el cliente.');
    if (this.form.get('date').invalid) messages.push('Indica la fecha.');
    if (outsideFiscalYear) messages.push('La fecha no pertenece al año fiscal seleccionado.');
    if (this.form.get('typePay').invalid) messages.push(this.isSupplier ? 'Selecciona el tipo de pago.' : 'Selecciona el tipo de cobro.');
    if (this.form.get('cashRegisterUid').invalid) messages.push('Selecciona la caja.');
    if (this.form.get('bankAccountUid').invalid) messages.push('Selecciona la cuenta bancaria.');
    if (this.form.get('partnerUid').invalid) messages.push('Selecciona el socio.');
    if (this.form.get('paymentCardUid').invalid) messages.push('Selecciona la tarjeta.');
    if (this.form.get('duration').invalid) messages.push('Selecciona la duración de la fianza.');
    if (this.lines.length === 0) messages.push('Añade al menos una línea de concepto.');

    this.lines.controls.forEach((line, index) => {
      if (line.get('conceptUid').invalid) messages.push(`Línea ${index + 1}: indica el concepto.`);
      if (line.get('quantity').invalid) messages.push(`Línea ${index + 1}: indica la cantidad.`);
      if (line.get('price').invalid) messages.push(`Línea ${index + 1}: indica el precio.`);
    });

    const shown = messages.slice(0, 6).map(message => '• ' + message);
    const rest = messages.length - shown.length;
    if (rest > 0) shown.push(`• y ${rest} más`);
    this.notification.errorBottom('Revisa los campos marcados en rojo:\n' + shown.join('\n'));

    this.visibleCount = this.lines.length;
    setTimeout(() => {
      const firstInvalid = document.querySelector('app-customer-advance-payment-form .mat-form-field-invalid, app-customer-advance-payment-form .mat-mdc-form-field-invalid, app-customer-advance-payment-form .pf-empty-lines--invalid');
      if (firstInvalid) firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  onCancel(): void {
    this.router.navigate([this.listRoute]);
  }

  recomputeTotals(): void {
    const values = this.lines.getRawValue() || [];
    let subtotal = 0;
    let taxesTotal = 0;
    let retentionsTotal = 0;
    let hasIVA = false;
    let hasIGIC = false;
    let hasAIEM = false;
    const lineTotals: number[] = [];

    for (const line of values) {
      const base = (Number(line.price) || 0) * (Number(line.quantity) || 0) * (1 - (Number(line.discount) || 0) / 100);
      subtotal += base;
      let lineSum = base;

      for (const selected of line.tax || []) {
        const tax = this.findTaxFull(selected);
        const rate = tax && tax.metadata && tax.metadata.import != null ? Number(tax.metadata.import) : 0;
        const amount = base * (rate / 100);
        lineSum += amount;
        if (rate > 0) taxesTotal += amount;
        else if (rate < 0) retentionsTotal += amount;

        const type = tax && tax.metadata && tax.metadata.type ? String(tax.metadata.type).toUpperCase() : '';
        if (type.indexOf('AIEM') !== -1) hasAIEM = true;
        if (type.indexOf('IGIC') !== -1) hasIGIC = true;
        if (type.indexOf('IVA') !== -1) hasIVA = true;
      }

      lineTotals.push(lineSum);
    }

    this.subtotal = subtotal;
    this.taxesTotal = taxesTotal;
    this.retentionsTotal = retentionsTotal;
    this.total = subtotal + taxesTotal + retentionsTotal;
    this.lineTotals = lineTotals;
    this.taxesLabel = hasIVA ? 'IVA' : hasIGIC ? 'IGIC' : hasAIEM ? 'AIEM' : 'Impuestos';
  }

  private buildTaxIndex(): void {
    this.taxById = new Map<string, any>();
    for (const tax of this.taxes) {
      if (tax && tax.id != null) this.taxById.set(String(tax.id).toLowerCase(), tax);
    }
  }

  private findTaxFull(tx: any): any {
    if (!tx) return null;
    const id = tx.id != null ? tx.id : tx.uid;
    return id != null ? this.taxById.get(String(id).toLowerCase()) || null : null;
  }

  handleTaxFocus = (lineIndex: number): void => {
    const line = this.lines.at(lineIndex);
    if (!line) return;
    this.prevTaxesByLineKey[this.getLineKey(line)] = JSON.parse(JSON.stringify(line.get('tax').value || []));
  }

  handleTaxChange = (value: any, lineIndex: number): string => {
    const line = this.lines.at(lineIndex);
    if (!line) return '';

    const ctrl = line.get('tax');
    const key = this.getLineKey(line);
    const current = ctrl.value || [];

    if (!this.lastValidTaxesByLineKey[key]) {
      this.lastValidTaxesByLineKey[key] = JSON.parse(JSON.stringify(current));
    }

    const validation = this.validateTaxesSelection(current);
    if (!validation.ok) {
      const previous = this.prevTaxesByLineKey[key] || this.lastValidTaxesByLineKey[key];
      ctrl.setValue(JSON.parse(JSON.stringify(previous)), { emitEvent: true });
      if (validation.message) this.notification.errorBottom(validation.message);
      return '';
    }

    this.lastValidTaxesByLineKey[key] = JSON.parse(JSON.stringify(current));
    return '';
  }

  private getLineKey(line: any): string {
    if (!line.__taxKey) {
      line.__taxKey = 'l_' + Math.random().toString(16).slice(2) + '_' + Date.now();
    }
    return String(line.__taxKey);
  }

  private validateTaxesSelection(selected: any[]): { ok: boolean; message: string } {
    const used: any = {};
    const mainTypeUsed: any = {};

    for (const tax of selected || []) {
      if (!tax) continue;
      const id = tax.id != null ? tax.id : tax.uid;
      if (id == null) continue;

      if (used[id]) return { ok: false, message: 'Este impuesto ya ha sido aplicado.' };

      const type = tax.metadata && tax.metadata.type ? String(tax.metadata.type) : '';
      const name = tax.metadata && tax.metadata.name ? String(tax.metadata.name).toLowerCase() : '';
      const isSpecial = name.indexOf('recargo equiv') >= 0 || name.indexOf('recargo de equiv') >= 0 || name.indexOf('inversión s.p.') >= 0;

      if (!isSpecial && type) {
        if (mainTypeUsed[type]) return { ok: false, message: 'Ya se ha aplicado un impuesto del tipo ' + type + '.' };
        mainTypeUsed[type] = true;
      }

      used[id] = true;
    }

    return { ok: true, message: '' };
  }

  private sameId(a: any, b: any): boolean {
    return a != null && b != null && String(a).toLowerCase() === String(b).toLowerCase();
  }

  public trackByIndex(i: number) { return i; }
}
